/**
 * FCM web push: permission, token register, foreground messages.
 */

import * as React from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { useQueryClient } from '@tanstack/react-query';
import { messaging } from '@/config/firebase';
import { NOTIFICATION_QUERY_KEYS } from '@/constants/shared-catalog';
import { notificationsApi } from '@/services/notifications.service';

/** sessionStorage / localStorage key for the last registered FCM token. */
const PUSH_TOKEN_STORAGE_KEY = 'fcm_push_token';

/** Registers FCM web push and invalidates notification queries on foreground messages. */
export function usePushNotifications(enabled = true): void {
  const queryClient = useQueryClient();
  // useRef: avoids re-running setup when a state flag would retrigger the effect
  const setupDoneRef = React.useRef(false);

  React.useEffect(() => {
    if (!enabled || !messaging) {
      return;
    }

    if (setupDoneRef.current) {
      return;
    }

    const setupPushNotifications = async () => {
      try {
        if (!('Notification' in window)) {
          console.warn('[usePushNotifications] Notifications not supported in this browser');
          return;
        }

        let permission = Notification.permission;

        if (permission === 'default') {
          permission = await Notification.requestPermission();
        }

        if (permission !== 'granted') {
          console.log('[usePushNotifications] Notification permission denied');
          return;
        }

        // Done even if token fails — do not retry on every re-render
        setupDoneRef.current = true;

        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/',
        });

        console.log('[usePushNotifications] Service worker registered');

        await navigator.serviceWorker.ready;

        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
        if (!vapidKey) {
          console.error('[usePushNotifications] VITE_FIREBASE_VAPID_KEY not configured');
          return;
        }

        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: registration,
        });

        if (token) {
          console.log('[usePushNotifications] FCM token obtained');

          sessionStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);

          await notificationsApi.registerPushToken(token);
          console.log('[usePushNotifications] Token registered with backend');
        } else {
          console.warn('[usePushNotifications] No FCM token available');
        }
      } catch (error) {
        console.error('[usePushNotifications] Setup failed:', error);
      }
    };

    void setupPushNotifications();

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('[usePushNotifications] Foreground message received', payload);

      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.unreadCount });

      if (payload.notification && document.visibilityState !== 'visible') {
        new Notification(payload.notification.title || 'Nouvelle notification', {
          body: payload.notification.body || '',
          icon: '/favicon.ico',
          tag: payload.data?.notificationId || 'ednov-notification',
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [enabled, queryClient]);
}

/** Session-stored FCM token used to unregister the device on logout. */
export function getPushToken(): string | null {
  return sessionStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
}

/** Drops the session-stored FCM token after logout unregister. */
export function clearPushToken(): void {
  sessionStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
}
